import Storage from '@/services/storageLib'
import agentDeskProperties from '@/services/liveAgent/constants/agentDeskProperties'
import { sendAgentInfo, responseAnalyzerSendUserMessage } from '@/services/webhook/webhook'
import AppWarn from '@/services/errors/app-warns'
import { botResponseMessageParser } from '@/services/parser/botResponseParser'
import { GlobalVars } from '@/services/globals'
import { isTypeBOT, isUserLoggedIn, lazyLoadLiveAgent, checkForGreetingsAndResetCookie,
  checkRenewTokenRequired, generateAttributes, requestFormatter, addCustomErrors, loadBotDetails, clearIdleTimerForSurveyRule, getIdleTimeout, clearTimerForAgentDeskIdleTimeout, emitEventToParentWindow, emitCustomFieldsToParent } from '@/services/utils'
import { checkHumanHandoffTriggered, captureInfoLogs, checkLiveAgentDeskName } from '@/services/liveAgent/utils/utils'
import { constantStrings, customAttributes, eventType } from '@/services/constants'
import store from '..'
import TextAccumulatorUtil from '../../services/textAccumulator'

export default {
    async sendAgentEventInfo({ dispatch, rootState }, data) {
        try {
          dispatch('clearItemsBeforeMessageParsing')
          const isAppStateOnly = data.isAppStateOnly || false
          const CUSTOM_ATTRIBUTES = generateAttributes(data)
          const requestBody = {
            ...requestFormatter(rootState, data),
            additionalAttributes: {
              CUSTOM_ATTRIBUTES
            },
            messageType: data.messageType
          }

          dispatch('showDayDateSeparator', requestBody)
          let userMessageReq
          if (store.getters.isResponseAnalyzerEnabled) {
            const messageId = requestBody?.messagePayload?.messageId
            const responseAnalyzerMsgId = `resp_analyser_${messageId}`
            requestBody.messagePayload.messageId = responseAnalyzerMsgId
            userMessageReq = !isAppStateOnly && await responseAnalyzerSendUserMessage({botRefId :rootState.botRefId, botId: rootState.promptTestDetails?.botId, requestBody, socialChannel:  rootState.promptTestDetails?.socialChannel})
            emitEventToParentWindow(eventType.USER_QUERY_SENT,{ conversationId: requestBody?.conversationId, userQuery: requestBody?.messagePayload?.label, messageId: requestBody?.messagePayload?.messageId, backToBotQuery: true})
          } else {
            userMessageReq = !isAppStateOnly && await sendAgentInfo({botRefId :rootState.botRefId ,requestBody})
          }
          rootState.widgetConfig?.isAuthenticationEnabled && checkRenewTokenRequired(userMessageReq.data, dispatch)
        } catch (err) {
          dispatch('updateTypingIndicatorStatus', false)
          // If message is a greeting, set cookie to false to retry again if user comes online from offline, also don't throw app error
          if (checkForGreetingsAndResetCookie(data)) {
            dispatch('clearChats')
            return
          }
          throw new AppWarn({
            name: 'Action',
            message:`Error while sending agent info: ${err.message}`,
            error: addCustomErrors(err) || {}
          })
        }
    },
    async sendSurveyFormData(_,data){
      if( checkLiveAgentDeskName(constantStrings.ZENDESK) && !isTypeBOT(data.ownerType)){
        GlobalVars.$liveAgent.sendFeedbackDataToLiveAgent(data) 
      }
    },

    async receiveAgentDeskEvents(_, data) {
      //process agent desk events here
      GlobalVars.$liveAgent.processResponseCodes(data)
    },

    async receiveAgentDeskMessages({ rootState, commit, getters, dispatch }, data) {
      dispatch('updateTypingIndicatorStatus', false)
      const customFields = data.customFields
      const isAgentResponse = rootState.controlType === data.ownerType ? !isTypeBOT(data.ownerType) : false
      const botResponse = botResponseMessageParser(data, true, isAgentResponse)
      if (checkHumanHandoffTriggered(data.ownerType)) {
        emitCustomFieldsToParent(customFields)
        captureInfoLogs('Live Agent Created', 'Creating Live agentdesk instance')
        dispatch('clearFallbackMechanismTimer')
        if (getters.isStreamingEnabled) {
          botResponse?.messages?.forEach(message => {
            message.messageValue.isStreaming = true
          })
        }
        commit('UPDATE_CHATS', botResponse)
        lazyLoadLiveAgent()
        checkLiveAgentDeskName(constantStrings.ZENDESK) && await loadBotDetails()
        isUserLoggedIn() && await this.updateAgentDeskConnectionStatus({connectionStatus: 'connected'})
        clearIdleTimerForSurveyRule()
        setTimeout(() => {
          if(store.state.isVoiceModeActive) {
            TextAccumulatorUtil.playLastChunk()
          }
        }, 5000);
      } else {
        // check response codes for generic agentdesk
       if(!checkLiveAgentDeskName(constantStrings.ZENDESK)){
        GlobalVars.$liveAgent.processResponseHandlingCodes(customFields)
       }    
        if (data?.agentDesk?.toUpperCase() !== constantStrings.SALESFORCE) botResponse.agentInfo = rootState.agentInfo
        if (getters.isStreamingEnabled) {
          botResponse?.messages?.forEach(message => {
            message.messageValue.isStreaming = true
          })
        }
        commit('UPDATE_CHATS', botResponse)
      }
      dispatch('throttleIdleChatTimeout') // Throttle chat session timeout
    },

    throttleAgentDeskConnectionIdleTimeout({ commit, dispatch, rootState }, delay) {
      const idleTimeout = getIdleTimeout()
      if (!isTypeBOT(rootState.controlType) && idleTimeout > delay) {
        clearTimerForAgentDeskIdleTimeout()
        const timerId = setTimeout(() => {
          !isTypeBOT(rootState.controlType) && dispatch('handleBackToBotTrigger', customAttributes.triggerResponse())
        }, delay)
        commit('UPDATE_AGENT_DESK_IDLE_TIMEOUT_ID', timerId)
      }
    },

    updateAgentDeskConnectionStatus({ commit }, data) {
        commit('UPDATE_AGENT_DESK_CONNECTION_STATUS', data)
    },

    updateDisplayNameForAgentDeskSystem({ commit }, data) {
        commit('UPDATE_DISPLAY_NAME_FOR_AGENTDESK', data)
    },

    updateControlType({ commit }, data) {
        commit('UPDATE_CONTROL_TYPE', data)
    },

    loadChatsWithAgentDesk ({ dispatch }) {
        const chats = Storage.getLocalStorageData(agentDeskProperties.AGENT_DESK_CHATS_STORAGE_KEY)
        if (chats) {
        chats.forEach((chat) => dispatch('updateChats', chat))
            dispatch('sortChatsAccToTimestamp')
        }
    },

    clearChatsWithAgentDesk () {
        Storage.removeLocalStorageData(agentDeskProperties.AGENT_DESK_CHATS_STORAGE_KEY)
    },

    updateChatsWithAgentDesk (_, data) {
        const chats = Storage.getLocalStorageData(agentDeskProperties.AGENT_DESK_CHATS_STORAGE_KEY)
        Storage.setLocalStorageData(agentDeskProperties.AGENT_DESK_CHATS_STORAGE_KEY, chats ? [ ...chats, data] : [data])
    },

    updateChats ({ commit }, data) {
        commit('UPDATE_CHATS', data)
    },

    sortChatsAccToTimestamp({ commit }) {
        commit('SORT_CHATS_ACC_TO_TIMESTAMP')
    },

    updateVisitorInfo({ commit }, data) {
      commit('UPDATE_VISITOR_INFO', data)
    },

    addTags({ commit }, data) {
      commit('UPDATE_MESSAGE_TAGS', data)
    },

    clearTags({ commit }) {
        commit('UPDATE_MESSAGE_TAGS')
    },

    updateEndChatCustomFieldStatus ({ commit }, data) {
        commit('UPDATE_END_CHAT_STATUS', data)
    },

    updateAgentInfo ({ commit }, data) {
        commit('UPDATE_AGENT_INFO', data)
    },

    clearVisitorInfo ({ commit }) {
        commit('CLEAR_VISITOR_INFO')
    },

    updateHandoffDepartment ({ commit }, data) {
        commit('UPDATE_HANDOFF_DEPARTMENT', data)
    }
}
